import { useLocation, useNavigate } from 'react-router-dom'
import React, { createContext, useEffect, useState, useContext } from "react";
import { Box, Container, Button } from "@mui/material";
import QMentisBreadCrumb from '../../custom-components/QMentisBreadCrumb';
import { Typography } from '@mui/material'
import { ReactComponent as AutomateIcon } from "../../../images/automateIcon.svg";
import FullscreenToggle from "../../custom-components/FullScreenToggle";
import TestCaseUI from '../../output-components/test-automation/TestCaseImported';
import Feature from '../../output-components/test-automation/FeatureFilesCreated';
import InputForm from '../../output-components/test-automation/PageSource';
import PageClass from '../../output-components/test-automation/PageClass';
import Locators from '../../output-components/test-automation/Locators';
import StepDefinition from '../../output-components/test-automation/StepDefinition';
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded';
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { cypressTestFilesWithToolCalling, cypressPageClassAPIWithToolCalling, cypressProjectAPIWithToolCalling } from '../../../services/webServices';
import { LoadingModalContext } from "../../main-pages/ServicePage";
import UploadBox from '../input-pages/UploadBox';
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import { downloadCypressProject } from "../../download/cypressProjectDownload";
import TestEnvironmentForm from '../../output-components/test-automation/TestEnvironment';
import MessagePopup from '../../popup-components/MessagePopup';

export const TestCaseFlowDataContext = createContext(null);

const CypressJavaScriptModifiedTestCaseOutputPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [testEnvironment, setTestEnviorment] = useState({});
  const [testCase, setTestCase] = useState({});
  const [testFiles, setTestFiles] = useState('');
  const [pageSource, setPageSource] = useState('');
  const [locator, setLocator] = useState('');
  const [pageClass, setPageClass] = useState('');
  const [stepDefinition, setstepDefinition] = useState('');
  const {setLoadingState} = useContext(LoadingModalContext);

  const [confirmed, setConfirmed] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [projectType, setProjectType] = useState('');
  
  useEffect(() => {
    console.log(location.state, "cypress-javascript-modified-testcase");
    setProjectType(location.state?.projectType || "");
    setTestEnviorment(location.state?.testenvironmentdata || {});
    setTestCase(location.state?.testCaseData || {});
    setTestFiles(location.state?.testFilesData || "");
    setPageSource(location.state?.pageSource || "");
    setLocator(location.state?.generatedLocators || {});
    setPageClass(location.state?.generatedPageclassData || "");
    setstepDefinition(location.state?.generatedStepDefinitionData || "");
  }, [location.state]);

  const breadCrumbs = [
    {
      title: "Set Test Environment",
      status: Object.entries(testEnvironment).length !== 0 || confirmed ? "Completed" : "In Progress",
    },
    {
      title: "Test Case Details",
      status: Object.entries(testCase).length > 0
        ? "Completed"
        : Object.entries(testEnvironment).length !== 0 && Object.entries(testCase).length === 0
          ? "In Progress"
          : "Not Started",
    },
    {
      title: "Test Files",
      status:
        !!testFiles && testFiles !== ""
          ? "Completed"
          : Object.entries(testCase).length > 0
            ? "In Progress"
            : "Not Started",
    },
    {
      title: "Introduce SUT",
      status:
        !!pageSource && pageSource !== ""
          ? "Completed"
          : !!testFiles && testFiles !== ""
            ? "In Progress"
            : "Not Started",
    },
    {
      title: "Generate Locators",
      status:
        Object.entries(locator).length > 0
          ? "Completed"
          : !!pageSource && pageSource !== ""
            ? "In Progress"
            : "Not Started",
    },
    {
      title: "Page Objects",
      status:
        !!pageClass && pageClass !== ""
          ? "Completed"
          : Object.entries(locator).length > 0
            ? "In Progress"
            : "Not Started",
    },
    {
      title: "Test Cases",
      status:
        !!stepDefinition && stepDefinition !== ""
          ? "Completed"
          : !!pageClass && pageClass !== ""
            ? "In Progress"
            : "Not Started",
    },
  ];

  const handleGenerateTestCases = async () => {
    try {
      const startProgress = (setProgress) => {
        setProgress(10);
        setTimeout(() => setProgress(30), 500);
        setTimeout(() => setProgress(50), 1000);
        setTimeout(() => setProgress(70), 1500);
        setTimeout(() => setProgress(90), 2000);
      };

      const finishTo100AndClose = async () => {
        return new Promise((resolve) => {
          setTimeout(() => {
            setLoadingState((prev) => ({ ...prev, progress: 100 }));
            setTimeout(() => {
              setLoadingState({ open: false, message: "", progress: null });
              resolve();
            }, 500);
          }, 500);
        });
      };

      setLoadingState({ open: true, message: "Generating Feature Files...", progress: 0 });
      startProgress((progress) => setLoadingState((prev) => ({ ...prev, progress })));

      const testFilesGenerated = await cypressTestFilesWithToolCalling({ testCaseData: testCase }, location.state?.framework);
      const generatedTestFiles = testFilesGenerated.data.data; // Extract the data array from the response
      await finishTo100AndClose();
      navigate(location.pathname, { state: { ...location.state, testFilesData: generatedTestFiles }, replace: true });
    }
    catch (error) {
      const errorMsg = error?.response?.data?.Error || "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      if (error.response?.data?.Error === "Your session has expired.") {
        // Handle session expired
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
    finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handlePageClass = async () => {
    try {
      const startProgress = (setProgress) => {
        setProgress(10);
        setTimeout(() => setProgress(30), 500);
        setTimeout(() => setProgress(50), 1000);
        setTimeout(() => setProgress(70), 1500);
        setTimeout(() => setProgress(90), 2000);
      };

      const finishTo100AndClose = async () => {
        return new Promise((resolve) => {
          setTimeout(() => {
            setLoadingState((prev) => ({ ...prev, progress: 100 }));
            setTimeout(() => {
              setLoadingState({ open: false, message: "", progress: null });
              resolve();
            }, 500);
          }, 500);
        });
      };

      setLoadingState({ open: true, message: "Generating Page Classes...", progress: 0 });
      startProgress((progress) => setLoadingState((prev) => ({ ...prev, progress })));

      const generatedPageClassGenerated = await cypressPageClassAPIWithToolCalling({ testCaseDetails: testFiles, locators: locator }, location.state?.framework);
      const generatedPageClass = generatedPageClassGenerated.data.data; // Extract the data array from the response
      await finishTo100AndClose();
      navigate(location.pathname, { state: { ...location.state, generatedPageclassData: generatedPageClass }, replace: true });
    }
    catch (error) {
      const errorMsg = error?.response?.data?.Error || "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      if (error.response?.data?.Error === "Your session has expired.") {
        // Handle session expired
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
    finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleStepDefinition = async () => {
    try {
      setLoadingState({ open: true, message: "Generating Test Cases...", progress: 0 });
      const startProgress = (setProgress) => {
        setProgress(10);
        setTimeout(() => setProgress(30), 500);
        setTimeout(() => setProgress(50), 1000);
        setTimeout(() => setProgress(70), 1500);
        setTimeout(() => setProgress(90), 2000);
      };

      const finishTo100AndClose = async () => {
        return new Promise((resolve) => {
          setTimeout(() => {
            setLoadingState((prev) => ({ ...prev, progress: 100 }));
            setTimeout(() => {
              setLoadingState({ open: false, message: "", progress: null });
              resolve();
            }, 500);
          }, 500);
        });
      };

      startProgress((progress) => setLoadingState((prev) => ({ ...prev, progress })));

      const generatedStepDefinitionGenerated = await cypressProjectAPIWithToolCalling({ testFilesGenerated: testFiles, pageClassData: { "pageClassGenerated": pageClass } }, location.state?.framework);
      const generatedStepDefinition = generatedStepDefinitionGenerated.data;
      await finishTo100AndClose();
      navigate(location.pathname, { state: { ...location.state, generatedStepDefinitionData: generatedStepDefinition }, replace: true });
    }
    catch (error) {
      const errorMsg = error?.response?.data?.Error || "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      if (error.response?.data?.Error === "Your session has expired.") {
        // Handle session expired
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
    finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };



  const handleDownloadClick = () => {
    if (stepDefinition && stepDefinition !== "") {
      // Download the Cypress project
      const success = downloadCypressProject(stepDefinition);
      if (success) {
        setPopupMessage("Cypress project downloaded successfully!");
        setPopupOpen(true);
      } else {
        setPopupMessage("Error downloading Cypress project. Please try again.");
        setPopupOpen(true);
      }
    } else {
      setPopupMessage("Please generate the Cypress project first.");
      setPopupOpen(true);
    }
  };

  const testcaseNextButton = {
    label: "Generate Test Files",
    onClick: handleGenerateTestCases,
    startIcon: <GenerateButton />,
    variant: "contained",
    sx: { float: "right" },
  };

  const cypressDownloadButton = {
    label: "Download",
    onClick: handleDownloadClick,
    variant: "text",
    startIcon: <SaveAltRoundedIcon />,
    sx: { textTransform: "none" },
  };

  const locatorsNextButton = {
    label: "Generate Page Classes",
    variant: "contained",
    onClick: handlePageClass,
    startIcon: <GenerateButton />,
    sx: { float: "right" },
  };

  const stepDefinitionNextButton = {
    label: "Generate Cypress Project",
    variant: "contained",
    onClick: handleStepDefinition,
    startIcon: <GenerateButton />,
    sx: { float: "right" },
  };



  const formatTestPlan = (testPlanData) => {
    const formattedTestPlan = {};
    Object.entries(testPlanData).map(([key, value]) => {
      if (Array.isArray(value)) {
        return (formattedTestPlan[key] = flattenArray(value));
      }
      if (!!value && typeof value === "object") {
        return (formattedTestPlan[key] = flattenObject(value));
      }
      return (formattedTestPlan[key] = value);
    });
    return formattedTestPlan;
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexGrow: 1,
      }}
    >
      <Box sx={{ maxHeight: "100%", width: "100%" }}>
        {/* Fixed breadcrumb where you want it */}
        <Box
          sx={{
            position: "sticky",
            top: "0",
            width: "100%",
            zIndex: 1000,
            bgcolor: "white",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <QMentisBreadCrumb breadCrumbs={breadCrumbs} sx={{ height: "56px" }} />
        </Box>
        <Box
          sx={{
            display: "flex",
            bgcolor: "#f6f3f2",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            overflowY: "auto",
            width: "100%",
            height: "80vh",
            minHeight: "80vh",
            position: "relative",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "4px",
              minHeight: "20px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "600px",
              margin: "10px auto",
              "& > *:not(:last-child)": {
                marginBottom: "16px",
              },
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={0.1}
            >
              <Box display="flex" alignItems="center" mb={0.1}>
                <AutomateIcon style={{ marginRight: 4 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ mt: 0.5 }}
                >
                  Generate automated test scripts
                </Typography>
              </Box>
              <FullscreenToggle />
            </Box>

            {/* CYPRESS JAVASCRIPT MODIFIED TEST CASE COMPONENTS */}
            <TestEnvironmentForm
              testEnvironmentData={testEnvironment}
              setTestEnviorment={setTestEnviorment}
              framework={location.state?.framework}
              projectType={location.state?.projectType}
              confirmed={confirmed}
              setConfirmed={setConfirmed}
            />

            {confirmed && (
              <UploadBox
                testCaseData={testCase}
                setTestCase={setTestCase}
                testcaseNextButton={testcaseNextButton}
                javaSeliniumDownloadButton={cypressDownloadButton}
              />
            )}
            {Object.entries(testCase).length > 0 && <TestCaseUI testCaseData={testCase} setTestCase={setTestCase} testcaseNextButton={testcaseNextButton} javaSeliniumDownloadButton={cypressDownloadButton} projectType={projectType} />}
            {!!testFiles && testFiles !== "" && <Feature featureFile={testFiles} setFeatureFile={setTestFiles} projectType={projectType}/>}
                                    {!!testFiles && testFiles !== "" && <InputForm setPageSource={setPageSource} framework={location.state?.framework} />}
            {Object.entries(locator).length > 0 && <Locators locator={locator} setLocator={setLocator} locatorsNextButton={locatorsNextButton} projectType={projectType}/>}
                          {!!pageClass && pageClass !== "" && <PageClass pageClass={pageClass} setPageClass={setPageClass} featureFile={testFiles}
              stepDefinitionNextButton={stepDefinitionNextButton}
              projectType={projectType}
            />}
            {!!stepDefinition && stepDefinition !== "" && (
              <Box sx={{ backgroundColor: '#fff', borderRadius: 1, p: 2, marginTop: '-15px' }}>
                <Typography variant="h6" gutterBottom>
                  Cypress Project Generated Successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Your Cypress JavaScript project has been generated. You can now download the complete project structure.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleDownloadClick}
                    startIcon={<SaveAltRoundedIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Download
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Container
          sx={{
            width: "100%",
          }}
        >
        </Container>
      </Box>
      <Box
        component="footer"
        sx={{
          width: "100%",
          maxWidth: "601px",
          backgroundColor: "#f6f3f2",
          textAlign: "center",
          paddingX: "5px",
          position: "fixed",
          bottom: "0",
          zIndex: "99",
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Use QMentisAI™ with human supervision, as with all generative AI tools.
        </Typography>
      </Box>
      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage}
      />

    </Box>
  );
};

export default CypressJavaScriptModifiedTestCaseOutputPage; 